import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { X, Check, Copy, ArrowRight } from "lucide-react";
import { generateShowUpMessage, getSettings, getLeads, saveLeads, CLOSER_ACCOUNTS } from "./demoConfig";
import { updateLeadField } from "./api";
import WelcomeImageCard from "./WelcomeImageCard";

export default function PreDemoDrawer({ lead, auth, onClose, onStartDemo }) {
  const settings = getSettings();
  const [meetLink, setMeetLink] = useState(settings.defaultMeetLink || "");
  const [time, setTime] = useState(
    lead.scheduledAt
      ? new Date(lead.scheduledAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
      : ""
  );
  const [closerUsername, setCloserUsername] = useState(lead.closerUsername || auth.username || "");
  const [copied, setCopied] = useState(false);
  const [checklist, setChecklist] = useState({
    showUp: false,
    gmail: false,
    chillApply: false,
    chillRefer: false,
    razorpay: false,
    zoom: false,
  });

  const allChecked = Object.values(checklist).every(Boolean);
  const closerObj = (CLOSER_ACCOUNTS || []).find(c => c.username === closerUsername);
  const closerName = closerObj?.name || auth.name;
  const message = generateShowUpMessage(lead, closerName, meetLink, time);

  const copyMessage = () => {
    navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleCheck = (key) => {
    setChecklist((c) => ({ ...c, [key]: !c[key] }));
  };

  const handleStartDemo = async () => {
    const leads = getLeads();
    const updates = { status: "demo_in_progress", closerUsername, closerName };
    const updated = leads.map((l) =>
      l.id === lead.id ? { ...l, ...updates } : l
    );
    saveLeads(updated);
    try { await updateLeadField(lead.id, updates); } catch (e) { /* fallback */ }
    onStartDemo();
  };

  return (
    <div className="predemo-overlay" onClick={onClose}>
      <div className="predemo-drawer" onClick={(e) => e.stopPropagation()} data-testid="predemo-drawer">
        <div className="predemo-header">
          <h2>Pre-demo: {lead.clientName} {lead.clientLastName}</h2>
          <button onClick={onClose} className="predemo-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="predemo-body">
          {/* Section A: Lead summary */}
          <div className="predemo-section">
            <h3 className="predemo-section-title">Lead summary</h3>
            <div className="predemo-summary">
              <div><strong>Name:</strong> {lead.clientName} {lead.clientLastName}</div>
              <div><strong>Role:</strong> {lead.targetRole}</div>
              <div><strong>Market:</strong> {lead.targetMarket}</div>
              <div><strong>Scheduled:</strong> {lead.scheduledAt ? new Date(lead.scheduledAt).toLocaleString() : "Not set"}</div>
            </div>
            <div className="demo-field" style={{ marginTop: 10 }}>
              <Label>Closer running this demo</Label>
              <select
                value={closerUsername}
                onChange={(e) => setCloserUsername(e.target.value)}
                data-testid="predemo-closer-select"
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 13, background: "#fff" }}
              >
                <option value="">— Pick closer —</option>
                {(CLOSER_ACCOUNTS || []).map(c => (
                  <option key={c.username} value={c.username}>{c.name} ({c.username})</option>
                ))}
              </select>
              <p style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>You can override later from Lead Detail.</p>
            </div>
          </div>

          {/* Section B: Message generator */}
          <div className="predemo-section">
            <h3 className="predemo-section-title">Show-up message</h3>
            <div className="predemo-inputs">
              <div className="demo-field">
                <Label>Zoom / Meet link</Label>
                <Input value={meetLink} onChange={(e) => setMeetLink(e.target.value)} placeholder="https://zoom.us/j/..." data-testid="meet-link-input" />
              </div>
              <div className="demo-field">
                <Label>Confirmed time</Label>
                <Input value={time} onChange={(e) => setTime(e.target.value)} placeholder="e.g. 3:00 PM" />
              </div>
            </div>

            <div className="whatsapp-bubble" data-testid="whatsapp-bubble">
              <pre className="whatsapp-text">{message}</pre>
            </div>

            <Button onClick={copyMessage} className="predemo-copy-btn" data-testid="copy-message-btn">
              {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy message</>}
            </Button>

            <WelcomeImageCard leadName={lead.clientName} closerName={closerName} />

            <p className="predemo-tip">
              Send this 30–60 minutes before the call. Remind them of the value, not just the time — this increases show-up rate.
            </p>
          </div>

          {/* Section C: Checklist */}
          <div className="predemo-section">
            <h3 className="predemo-section-title">Pre-call checklist</h3>
            <div className="predemo-checklist">
              {[
                { key: "showUp", label: "Show-up message sent" },
                { key: "gmail", label: "Client Gmail tab open and logged in" },
                { key: "chillApply", label: "ChillApply tab open" },
                { key: "chillRefer", label: "ChillRefer tab open" },
                { key: "razorpay", label: "Razorpay payment link ready to paste" },
                { key: "zoom", label: "Zoom/Meet started, camera on" },
              ].map((item) => (
                <label key={item.key} className="predemo-check-item">
                  <Checkbox
                    checked={checklist[item.key]}
                    onCheckedChange={() => toggleCheck(item.key)}
                    data-testid={`check-${item.key}`}
                  />
                  <span className={checklist[item.key] ? "checked-text" : ""}>{item.label}</span>
                </label>
              ))}
            </div>
            {allChecked && (
              <div className="predemo-ready-banner" data-testid="ready-banner">
                <Check className="w-5 h-5" /> You're ready. Start the demo when the client joins.
              </div>
            )}
          </div>
        </div>

        <div className="predemo-footer">
          <Button onClick={handleStartDemo} className="crm-start-demo-btn predemo-start" data-testid="drawer-start-demo">
            Start demo <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
